import React, { FunctionComponent } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Delete, Edit } from '@mui/icons-material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useConfirm } from 'material-ui-confirm';

import Table from '../../shared/Table';
import CenteredSpinner from '../../shared/CenteredSpinner';
import OverflowTooltip from '../../shared/OverflowTooltip';

import { ISubscription } from '../ManageInterModelSubscriptions';

interface ISubscriptionsListProps {
  modelName: string;
  instanceName: string;
  subscriptions: ISubscription[];
  isDataLoading: boolean;
  onReplicateSubscriptions: () => void;
  onEditSubscription: (subscription: ISubscription) => void;
  onCopySubscription: (subscription: ISubscription) => void;
  onDeleteSubscription: (subscription: ISubscription) => void;
  onNewSubscription: () => void;
  onViewHistory: () => void;
}

const SubscriptionsList: FunctionComponent<ISubscriptionsListProps> = ({
  modelName,
  instanceName,
  subscriptions,
  isDataLoading,
  onReplicateSubscriptions,
  onEditSubscription,
  onCopySubscription,
  onDeleteSubscription,
  onNewSubscription,
  onViewHistory,
}) => {
  const confirm = useConfirm();

  const handleDeleteClick = (subscription: ISubscription) => {
    confirm({
      title: 'Delete subscription?',
      description: `This removes the subscription for tag "${subscription.scenarioTag}". This cannot be undone.`,
      confirmationText: 'Delete',
    })
      .then(() => onDeleteSubscription(subscription))
      .catch(() => undefined);
  };

  const subscriptionColumns: ColDef[] = [
    {
      field: 'scenarioTag',
      headerName: 'Subscription Scenario Tag',
      flex: 1,
      minWidth: 250,
      // CLD-3040: full tag name on hover when it's clipped.
      cellRenderer: OverflowTooltip,
    },
    {
      field: 'status',
      headerName: 'Subscription Status',
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: 'Actions',
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams<ISubscription>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => params.data && onEditSubscription(params.data)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              onClick={() => params.data && onCopySubscription(params.data)}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => params.data && handleDeleteClick(params.data)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      {/* Header Row: Model, Instance, and Action Buttons */}
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <Box sx={{ display: 'flex', gap: 4, flex: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Model
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {modelName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Model Instance
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {instanceName}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {/* Replicate Subscriptions hidden for now - no requirements yet. */}
          <Button variant="outlined" size="small" onClick={onViewHistory}>
            History
          </Button>
          <Button variant="contained" size="small" onClick={onNewSubscription}>
            New Subscription
          </Button>
        </Box>
      </Box>

      {/* Subscriptions Table */}
      {isDataLoading ? (
        <CenteredSpinner />
      ) : subscriptions && subscriptions.length > 0 ? (
        <Table
          id="inter-model-subscriptions-table"
          isLoading={false}
          isSuccess
          pagination={subscriptions.length > 0}
          rowCount={subscriptions.length}
          rowData={subscriptions}
          columnDefs={subscriptionColumns}
          defaultColDef={{
            suppressAutoSize: true,
            suppressMenu: true,
            suppressSizeToFit: true,
          }}
        />
      ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No subscriptions found.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default SubscriptionsList;
